import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** Available chat window layouts. */
export type ChatDisplayMode = 'hidden' | 'sidebar' | 'fullscreen';

/** Chat layouts available outside the dedicated chat page. */
type PreferredChatMode = Exclude<ChatDisplayMode, 'fullscreen'>;

/** Keeps chat layout preference and route state synchronized. */
@Injectable({ providedIn: 'root' })
export class ChatWindowService {
  /** Services. */
  private readonly router = inject(Router);

  /** State. */
  private preferredMode: PreferredChatMode = 'sidebar';
  private lastPageUrl = this.router.url === '/chat' ? '/dashboard' : this.router.url;
  private readonly modeState = signal<ChatDisplayMode>(
    this.router.url === '/chat' ? 'fullscreen' : this.preferredMode
  );
  private readonly isChatRouteState = signal(this.router.url === '/chat');
  readonly mode = this.modeState.asReadonly();
  readonly isChatRoute = this.isChatRouteState.asReadonly();

  /** Constructor. */
  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.handleNavigation(event.urlAfterRedirects));
  }

  /** Changes the layout and keeps fullscreen in sync with its route. */
  setMode(mode: ChatDisplayMode): void {
    if (mode === 'fullscreen') {
      void this.router.navigateByUrl('/chat');
      return;
    }

    this.preferredMode = mode;
    this.modeState.set(mode);
    if (this.isChatRouteState()) void this.router.navigateByUrl(this.lastPageUrl);
  }

  /** Toggles the chat panel outside the fullscreen route. */
  togglePanel(): void {
    this.setMode(this.modeState() === 'sidebar' ? 'hidden' : 'sidebar');
  }

  /** Leaves fullscreen and restores the previous panel preference. */
  closeFullscreen(): void {
    if (this.isChatRouteState()) void this.router.navigateByUrl(this.lastPageUrl);
  }

  /** Synchronizes the layout when navigating through links or browser history. */
  private handleNavigation(url: string): void {
    const isChatRoute = url.split('?')[0] === '/chat';
    this.isChatRouteState.set(isChatRoute);
    if (isChatRoute) {
      this.modeState.set('fullscreen');
      return;
    }

    this.lastPageUrl = url;
    if (this.modeState() === 'fullscreen') this.modeState.set(this.preferredMode);
  }
}
